/**
 * Rule Categories Configuration
 * 
 * This file contains all available rule categories for the club rules engine.
 * To add new categories, simply add them to the appropriate group below.
 * 
 * Each category should have:
 * - value: Unique identifier (uppercase with underscores)
 * - label: Human-readable name
 * - color: Color theme (red, orange, yellow, green, blue, purple)
 * - description: Brief explanation of the category's purpose
 */

export const RULE_CATEGORIES = {
  // Core AML Categories - High Risk Financial Crime Prevention
  AML: [
    { value: 'AML', label: 'Anti-Money Laundering', color: 'red', description: 'Money laundering detection and prevention' },
    { value: 'CTF', label: 'Counter Terrorism Financing', color: 'red', description: 'Terrorism financing prevention' },
    { value: 'SANCTIONS', label: 'Sanctions Screening', color: 'red', description: 'Sanctions list monitoring' },
    { value: 'PEP', label: 'Politically Exposed Persons', color: 'orange', description: 'PEP identification and monitoring' },
  ],

  // Transaction Monitoring - Pattern Detection
  TRANSACTION_MONITORING: [
    { value: 'UAR', label: 'Unusual Activity Reporting', color: 'yellow', description: 'Suspicious transaction patterns' },
    { value: 'VELOCITY', label: 'Transaction Velocity', color: 'yellow', description: 'High frequency transaction monitoring' },
    { value: 'THRESHOLD', label: 'Threshold Monitoring', color: 'yellow', description: 'Large transaction detection' },
    { value: 'STRUCTURING', label: 'Structuring Detection', color: 'orange', description: 'Transaction structuring patterns' },
  ],

  // Responsible Gaming - Player Protection
  RESPONSIBLE_GAMING: [
    { value: 'RG', label: 'Responsible Gaming', color: 'green', description: 'Player protection and harm prevention' },
    { value: 'LOSS_LIMITS', label: 'Loss Limits', color: 'green', description: 'Player loss limit monitoring' },
    { value: 'TIME_LIMITS', label: 'Time Limits', color: 'green', description: 'Session time limit enforcement' },
    { value: 'SELF_EXCLUSION', label: 'Self Exclusion', color: 'green', description: 'Self-excluded player monitoring' },
  ],

  // Operational Risk - Security & Fraud
  OPERATIONAL_RISK: [
    { value: 'FRAUD', label: 'Fraud Detection', color: 'red', description: 'Fraudulent activity detection' },
    { value: 'IDENTITY', label: 'Identity Verification', color: 'blue', description: 'Customer identity validation' },
    { value: 'ACCOUNT_TAKEOVER', label: 'Account Takeover', color: 'red', description: 'Unauthorized account access' },
    { value: 'BONUS_ABUSE', label: 'Bonus Abuse', color: 'orange', description: 'Promotional offer abuse detection' },
  ],

  // Regulatory Compliance - Legal Requirements
  REGULATORY_COMPLIANCE: [
    { value: 'COMPLIANCE', label: 'Regulatory Compliance', color: 'blue', description: 'General regulatory requirements' },
    { value: 'KYC', label: 'Know Your Customer', color: 'blue', description: 'Customer due diligence' },
    { value: 'REPORTING', label: 'Regulatory Reporting', color: 'blue', description: 'Mandatory reporting requirements' },
    { value: 'RECORD_KEEPING', label: 'Record Keeping', color: 'blue', description: 'Transaction record maintenance' },
  ],

  // Gaming Specific - Club Operations
  GAMING_SPECIFIC: [
    { value: 'GAME_INTEGRITY', label: 'Game Integrity', color: 'purple', description: 'Game fairness and integrity' },
    { value: 'COLLUSION', label: 'Collusion Detection', color: 'red', description: 'Player collusion monitoring' },
    { value: 'CHIP_DUMPING', label: 'Chip Dumping', color: 'orange', description: 'Suspicious chip transfers' },
    { value: 'ADVANTAGE_PLAY', label: 'Advantage Play', color: 'yellow', description: 'Advanced playing techniques' },
  ]
}

// Flatten all categories into a single array for backward compatibility
export const CATEGORIES = Object.values(RULE_CATEGORIES).flat()

// Get categories by group
export const getCategoriesByGroup = (groupName) => {
  return RULE_CATEGORIES[groupName] || []
}

// Get all category values for validation
export const getAllCategoryValues = () => {
  return CATEGORIES.map(cat => cat.value)
}

// Get category by value
export const getCategoryByValue = (value) => {
  return CATEGORIES.find(cat => cat.value === value)
}

// Group labels for UI display
export const CATEGORY_GROUPS = {
  AML: 'Core AML Categories',
  TRANSACTION_MONITORING: 'Transaction Monitoring',
  RESPONSIBLE_GAMING: 'Responsible Gaming',
  OPERATIONAL_RISK: 'Operational Risk',
  REGULATORY_COMPLIANCE: 'Regulatory Compliance',
  GAMING_SPECIFIC: 'Gaming Specific'
}
